/**
 * Estado visual único de una mesa (card y ficha comparten la misma verdad).
 * Deriva de: table.status + occupiedAt + session + draft + needsLiberate
 * + reserva vencida + señales. Ver relevamiento H1/H18 y proto mesas-v2.
 */
export type MesaVisualState =
  | 'free'        // Libre — sin visita
  | 'looking'     // Mirando la carta (presencia, sin draft ni cuenta)
  | 'drafting'    // Armando — draft abierto sin cuenta
  | 'occupied'    // En mesa — sesión abierta
  | 'round'       // Nueva ronda — pagó parcial y sigue
  | 'paid'        // Pagada — cobrada, falta liberar
  | 'reserved'    // Reservada — turno vigente
  | 'overdue'     // Pasada — reserva vencida (visual; status sigue pending/confirmed)
  | 'closed';     // Cerrada — fuera de servicio

export type MesaVisualTone =
  | 'teal' | 'info' | 'warm' | 'ink' | 'violet' | 'danger' | 'muted';

export function mesaVisualStateMeta(
  state: MesaVisualState,
): { label: string; tone: MesaVisualTone } {
  switch (state) {
    case 'free':     return { label: 'Libre',        tone: 'teal' };
    case 'looking':  return { label: 'Mirando',      tone: 'info' };
    case 'drafting': return { label: 'Armando',      tone: 'warm' };
    case 'occupied': return { label: 'En mesa',      tone: 'ink' };
    case 'round':    return { label: 'Nueva ronda',  tone: 'violet' };
    case 'paid':     return { label: 'Pagada',       tone: 'warm' };
    case 'reserved': return { label: 'Reservada',    tone: 'info' };
    case 'overdue':  return { label: 'Pasada',       tone: 'danger' };
    case 'closed':   return { label: 'Cerrada',      tone: 'muted' };
  }
}

/** Deriva el estado visual a partir de los datos de la ficha/card. */
export function resolveMesaVisualState(args: {
  tableStatus: string;
  occupiedAt?: string | null;
  hasOpenSession: boolean;
  draftCount: number;
  lookingCount: number;
  needsLiberate: boolean;
  /** Sesión pagada completa (snapshot.paymentStatus==='paid'). */
  paidVisit?: boolean;
  /** Sesión con cobro parcial (snapshot.paymentStatus==='partial'). */
  partialPaid?: boolean;
  hasActiveReservation: boolean;
  reservationOverdue: boolean;
}): MesaVisualState {
  if (args.tableStatus === 'closed') return 'closed';
  if (args.tableStatus === 'reserved' || args.hasActiveReservation) {
    return args.reservationOverdue ? 'overdue' : 'reserved';
  }
  if (args.paidVisit) return 'paid';
  if (args.partialPaid) return 'round';
  if (args.needsLiberate && !args.hasOpenSession && args.draftCount === 0) return 'paid';
  if (args.needsLiberate && (args.hasOpenSession || args.draftCount > 0)) return 'round';
  if (args.hasOpenSession) return 'occupied';
  if (args.draftCount > 0) return 'drafting';
  if (args.lookingCount > 0) return 'looking';
  if (args.tableStatus === 'occupied' || args.occupiedAt != null) return 'occupied';
  return 'free';
}
